import { attributes, Model, type, AttributesMixin, define } from '@type-r/models';
import { fetchJson } from './endpoint';
import { InvoiceModel, InvoiceStatus } from './invoice';
import { ChangeTransaction } from './common';

export enum DisputeReason {
    Changed = 'Changed',
    QualityOfGoods = 'Quality of goods',
    MissingGoods = 'Missing goods',
    MissingCreditNote = 'Missing Credit Note',
    Discount = 'Discount',
    PartialPay = 'Partial Pay',
    PaymentPastDue = 'Payment Past due',
    Default = 'Default',
    Other = 'Other',
}

// export type DisputeReason = 'QualityOfGoods' |
//     'MissingGoods' |
//     'MissingCreditNote' |
//     'Discount' |
//     'PartialPay' |
//     'PaymentPastDue' |
//     'Default' |
//     'Other';

export type DisputeStatus =
    'Disputed' |
    'EscalatedInternally' |
    'Rejected' |
    'Approved' |
    'SentBack';

export type ActorRoles = 'Customer' | 'Supplier';



export type InvoiceDialogState = InstanceType<typeof InvoiceDialogState>;
export const InvoiceDialogState = attributes({
    invoiceId: String,
    emailTitle: String,
    toEmailList: Array,
    fromEmail: String,
    comment : type( String )
        .check(function(comment) {
            return this.reason !== 'Other' || comment;
        }, 'Required'),
    file : type( File ).null.dontSave,
    attachmentId: Number,
    reason : type( String ).required,
    issueCreditNote : false,
    change: Object,
    disputeTransaction: ChangeTransaction,
    onBehalfOf: String
});

export type DisputedInvoice = {
  invoiceId: String,
  emailTitle: String,
  toEmailList: [],
  fromEmail: String,
  comment: String,
  attachmentId: Number,
  reason :String,
  issueCreditNote : false,
  change: Object,
  disputeTransaction: ChangeTransaction
};

export interface InvoiceDisputeDetailHistory extends AttributesMixin<typeof InvoiceDisputeDetailHistory> {
    status: DisputeStatus;

    // who made the action
    actorRole: ActorRoles;

    activeSideRole: ActorRoles;
}

@define
export class InvoiceDisputeDetailHistory  extends Model {
    static attributes = {
        change: Object,
        comment: String,
        attachmentId: Number,
        createdOn: Date,
        versionNumber: Number,

        // who made the action
        actorRole: String,

        activeSideRole: String,

        status: String,
    };
}

export interface InvoiceDispute extends AttributesMixin<typeof InvoiceDispute> {
    initialInvoiceStatus: InvoiceStatus;
    reason: DisputeReason;
}

@define
export class InvoiceDispute extends Model {
    static attributes = {
        initialInvoiceStatus: String,
        reason: String,
        issueCreditNote: Boolean,
        history: [InvoiceDisputeDetailHistory]
    };

    get invoice(): InvoiceModel {
        return this.getOwner() as InvoiceModel;
    }

    get disputeSide(): ActorRoles | null {
        return this.history.length ?
            this.history.last().activeSideRole
            : null;
    }

    get isInternalEscalation(): boolean {
        return this.history.length
            && this.history.last().status === 'EscalatedInternally';
    }

    get isEscalatedByCustomer(): boolean{
        return this.history && this.history.length && this.history.last().actorRole === 'Customer';
    }

    get isEscalatedBySupplier(): boolean{
        return this.history && this.history.length && this.history.last().actorRole === 'Supplier';
    }

    get isOnlyInternalEscalationRejected(): boolean {
        return this.history.length === 2
            && this.history.first().status === 'EscalatedInternally'
            && this.history.last().status === 'Rejected';
    }

    get isOnlyInternalEscalation(): boolean {
        return this.history.length === 1
            && this.isInternalEscalation;
    }

    get isActiveDispute(): boolean {
        return this.history.length
            && this.history.last().status !== 'Approved'
            && this.history.last().status !== 'Rejected';
    }

    get isApproved(): boolean {
        return this.history.length
            && this.history.last().status === 'Approved';
    }

    get isUpdate(): boolean {
        return this.history.length === 1
            && this.history.last().status === 'Approved';
    }

    get isUserOnTheActiveSide(): boolean {
        if (!this.history.length) {
            return true;
        }

        const isFromTheSameSide = (this.history.last().activeSideRole === 'Customer') === this.invoice.isCustomer;
        return isFromTheSameSide;
    }

    getLastEntryWithStatus(status: DisputeStatus): InvoiceDisputeDetailHistory | undefined {
        const itemIndex = this.getLastIndexOf(x => x.status === status);
        return itemIndex >= 0 ?
            this.history.at(itemIndex) :
            undefined;
    }

    getLastEntry(
        predicate: (value: InvoiceDisputeDetailHistory) => boolean,
        startOffset: number = this.history.length
    ): InvoiceDisputeDetailHistory | undefined {
        const itemIndex = this.getLastIndexOf(predicate, startOffset);
        return itemIndex >= 0 ?
            this.history.at(itemIndex) :
            undefined;
    }

    getLastIndexOf(
        predicate: (value: InvoiceDisputeDetailHistory) => boolean,
        startOffset: number = this.history.length
    ): number {
        for (let i = startOffset - 1; i >= 0; --i) {
            const item = this.history.at(i);
            if (predicate(item)) {
                return i;
            }
        }

        return -1;
    }
}

export async function execDisputeAction(invoiceId, actionType, state: InvoiceDialogState) {
    const url = `/api/v2/invoices/${invoiceId}/dispute/${actionType}`;
    const data = state.toJSON();
    await fetchJson('POST', url, data);
}

export async function execDisputeActionBatch(actionType, invoices: DisputedInvoice[]) {
  const url = `/api/v2/invoices/dispute/${actionType}/batch`;
  return await fetchJson('POST', url, invoices);
}
